import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Formik } from "formik";
import styles from "./companyOnboading.module.css";
import ProgressBarOnboarding from "../../components/progressBarOnboarding/progressBarOnboarding";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import {
  useSaveOrUpdateCompanyMutation,
} from "../../services/api/onboarding.api";
import { addOrEditCompanyOnboardingInitialState, getOnboardingSteps } from "../../utils/constants";
import { newCompanyValidationSchema } from "../../utils/validation";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Modal from "../../components/modals/modal/Modal";
import CompanyExistPopup from "../../components/modals/companyExistPopup/companyExistPopup";
import ProvisioningOverlay from "../../components/provisioningOverlay/provisioningOverlay";
import { useCompanyProvisioning } from "../../hooks/useCompanyProvisioning";

import StepRenderer from "./stepRenderer";
import { TStage } from "../../types/types";
import { loadSavedProgress, clearProgress, saveProgress } from "../../utils/onboardingStorage";
import {
  isProceedButtonEnabled,
  getPreviousStep,
  getStepByNumber,
  getNextStep,
  saveCompanyData,
  isTestEnvironment,
  initializeFormState,
} from "./onboardingHelpers";

const CompanyOnboarding: React.FC = () => {
  const { selectedPlanId } = useParams<{ selectedPlanId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const languageId = searchParams.get('languageId');
  const steps = useMemo(() => getOnboardingSteps(), [i18n.language]);
  
  // Load saved progress from localStorage. Read once on mount: re-reading it on every render
  // made initialState change whenever progress was saved, and enableReinitialize then reset the
  // form mid-flow — which re-applied the URL plan id over the plan the user had just picked.
  const [savedProgress] = useState(loadSavedProgress);
  const [currentStep, setCurrentStep] = useState<TStage>(() => {
    if (savedProgress?.currentStepNumber) {
      const savedStep = getStepByNumber(savedProgress.currentStepNumber, steps);
      return savedStep || steps[0];
    }
    return steps[0];
  });
  const [saveOrUpdateCompany] = useSaveOrUpdateCompanyMutation();
  const [showCompanyExistPopup, setShowCompanyExistPopup] = useState<boolean>(false);
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [stepSaveError, setStepSaveError] = useState<string | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const formikRef = useRef<any>(null);
  const {
    overlay: provisioningOverlay,
    retryProvisioning,
    hideOverlay: hideProvisioningOverlay,
  } = useCompanyProvisioning();
  const provisioningGuidRef = useRef<string | undefined>(undefined);

  // Initialize form state with saved progress and URL parameters
  const initialState = useMemo(() => {
    const state = initializeFormState(savedProgress, selectedPlanId, languageId);
    return state;
  }, [selectedPlanId, languageId, savedProgress]);

  // Trigger validation when step changes to update isValid state
  useEffect(() => {
    if (formikRef.current && currentStep) {
      // Validate form to update isValid flag after step change
      formikRef.current.validateForm();
    }
    setStepSaveError(null);
  }, [currentStep]);

  // Clear localStorage when onboarding is complete
  useEffect(() => {
    if (currentStep?.stageNumber === 6) {
      clearProgress();
    }
  }, [currentStep]);


  const goToPrevStep = () => {
    if (!currentStep || !formikRef.current) return;
    // Immediately save to localStorage before navigating
    saveProgress(formikRef.current.values, currentStep.stageNumber);
    const prevStep = getPreviousStep(currentStep, steps);
    if (prevStep) setCurrentStep(prevStep);
  };

  const saveCompany = async (
    values: typeof addOrEditCompanyOnboardingInitialState,
    setValues: any
  ) => {
    return saveCompanyData(
      values,
      saveOrUpdateCompany,
      setValues,
      () => setShowCompanyExistPopup(true),
      currentStep.stageNumber
    );
  };

  // Steps 1-4 just persist the form data — the company shouldn't feel
  // "set up" yet, so no provisioning wait/overlay happens here. That
  // confirmation is deferred until payment succeeds (see finalizeProvisioning).
  const saveCurrentStepData = useCallback(
    async (
      values: typeof addOrEditCompanyOnboardingInitialState,
      setValues: any
    ) => {
      const { mergedData } = await saveCompany(values, setValues);
      provisioningGuidRef.current = mergedData.guid;
      return mergedData;
    },
    [currentStep.stageNumber]
  );

  // Runs once payment succeeds: this is the moment the company is
  // presented to the user as actually initialized.
  const finalizeProvisioning = useCallback(async () => {
    const guid = provisioningGuidRef.current || formikRef.current?.values?.guid;
    const canProceed = await retryProvisioning(guid);
    if (canProceed) {
      navigate("/onboarding/success"); //for facebook compaign tracking
    }
  }, [navigate, retryProvisioning]);

  const handleProvisioningExit = useCallback(() => {
    hideProvisioningOverlay();
  }, [hideProvisioningOverlay]);

  // Handle payment success - the company is only "revealed" as initialized now.
  // The card was already charged, so payment must not be resubmittable from
  // here on, even if the provisioning check below fails and offers a retry.
  const handlePaymentSuccess = async () => {
    setPaymentCompleted(true);
    if (formikRef.current) {
      // Immediately save to localStorage before navigating
      saveProgress(formikRef.current.values, currentStep.stageNumber);
    }
    await finalizeProvisioning();
  };

  const handleFormikSubmit = async (
    values: typeof addOrEditCompanyOnboardingInitialState,
    formikHelpers: any
  ) => {
    setStepSaveError(null);
    try {
      await saveCurrentStepData(values, formikHelpers.setValues);
    } catch (err: any) {
      // Check if the error is COMPANY_EXIST
      if (err?.data?.error === "COMPANY_EXIST") {
        setShowCompanyExistPopup(true);
      } else {
        setStepSaveError(t("ONBOARDING_SAVE_ERROR"));
      }
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const handleCloseCompanyExistPopup = () => {
    if (isTestEnvironment()) {
      // In test environment, close popup and move to next step
      setShowCompanyExistPopup(false);
      if (formikRef.current) {
        // Immediately save to localStorage before navigating
        saveProgress(formikRef.current.values, currentStep.stageNumber);
      }
      const nextStep = getNextStep(currentStep, steps);
      if (nextStep) {
        setCurrentStep(nextStep);
      }
    } 
  };

  return (
    <>
      <ProvisioningOverlay
        overlay={provisioningOverlay}
        onRetry={finalizeProvisioning}
        onExit={handleProvisioningExit}
      />
      {showCompanyExistPopup && (
        <Modal onClose={handleCloseCompanyExistPopup}>
          <CompanyExistPopup />
        </Modal>
      )}
      {steps && steps.length > 1 && currentStep && (
        <Formik
          initialValues={initialState}
          validationSchema={
            newCompanyValidationSchema[
              currentStep.stageNumber as keyof typeof newCompanyValidationSchema
            ]
          }
          enableReinitialize
          validateOnMount={true}
          validateOnChange={true}
          onSubmit={handleFormikSubmit}
        >
          {(formikProps) => {
            // Store formik instance in ref for useEffect access
            formikRef.current = formikProps;
            return (
              <form className={styles.onboardingForm} dir={i18n.dir()}>
               {currentStep.stageNumber > 0 && currentStep.stageNumber !== 6 && (<BackArrowButton
                    text={t("ONBOARDING_BACK")}
                    onClick={goToPrevStep}
                    position="right"
                  />
               )}
              <ProgressBarOnboarding steps={steps} currentStep={currentStep} />

              <StepRenderer
                stepNumber={currentStep.stageNumber}
                values={formikProps.values}
                setFieldValue={formikProps.setFieldValue}
                errors={formikProps.errors}
                touched={formikProps.touched}
                onPaymentSuccess={handlePaymentSuccess}
                languageId={languageId}
                paymentCompleted={paymentCompleted}
              />


             {currentStep.stageNumber < steps.length && currentStep.stageNumber !== 5 && currentStep.stageNumber !== 6 && (
                  <button
                    type="button"
                    className={`button ${styles.button} ${!isProceedButtonEnabled(formikProps, currentStep.stageNumber) || isSavingStep || provisioningOverlay.visible ? 'button_disabled' : 'hover'}`}
                    onClick={async () => {
                      const errors = await formikProps.validateForm();
                      const fields = Object.keys(errors);
                      formikProps.setTouched(
                        Object.fromEntries(fields.map((f) => [f, true]))
                      );
                      if (
                        fields.length === 0
                      ) {
                        setStepSaveError(null);
                        try {
                          setIsSavingStep(true);
                          const savedCompany = await saveCurrentStepData(
                            formikProps.values,
                            formikProps.setValues
                          );

                          saveProgress(savedCompany, currentStep.stageNumber);

                          const nextStep = getNextStep(currentStep, steps);
                          if (nextStep) {
                            setCurrentStep(nextStep);
                          }
                        } catch (err: any) {
                          // Check if the error is COMPANY_EXIST
                          if (err?.data?.error === "COMPANY_EXIST") {
                            setShowCompanyExistPopup(true);
                          } else {
                            setStepSaveError(t("ONBOARDING_SAVE_ERROR"));
                          }
                        } finally {
                          setIsSavingStep(false);
                        }
                      }
                    }}
                    disabled={
                      !isProceedButtonEnabled(formikProps, currentStep.stageNumber) ||
                      isSavingStep ||
                      provisioningOverlay.visible
                    }
                  >
                    {t("ONBOARDING_CONTINUE")}
                  </button>
                )}
              {stepSaveError && <p className="error">{stepSaveError}</p>}
            </form>
            );
          }}
        </Formik>
      )}
    </>
  );
};

export default CompanyOnboarding;
